import type { PropsWithChildren, RefObject } from 'react';
import { createContext, useContext, useMemo, useRef } from 'react';
import type { MapAPI, PointType } from '@elfsight/widget-map';
import { noop } from 'es-toolkit';
import { useLocationsFilter } from './locations-filter-provider';
import { useUserLocation } from './user-location-context';

export type IMapAPIContext = {
  mapAPIRef: RefObject<MapAPI | null> | undefined;
  setMapAPI: (mapAPIRef: MapAPI | null) => void;
  points: PointType[];
};

const MapAPIContext = createContext<IMapAPIContext>({
  mapAPIRef: undefined,
  setMapAPI: noop,
  points: []
});

export function MapAPIProvider({ children }: PropsWithChildren) {
  const mapAPIRef = useRef<MapAPI | null>(null);

  const { userLocation } = useUserLocation();
  const { currentLocations: locations } = useLocationsFilter();
  const { coordinates: userCoordinates } = userLocation ?? {};

  const fullLocations = useMemo(
    () =>
      !userCoordinates
        ? locations
        : [
            { id: 'user_location', place: { coordinates: userCoordinates } },
            ...locations
          ],
    [locations, userCoordinates]
  );

  const pointsInvalidateKey = fullLocations
    .map(
      ({ place, id }) =>
        `${id}:${place?.coordinates.lat},${place?.coordinates.lng}`
    )
    .join(';');

  const points = useMemo(
    () =>
      fullLocations
        .map(({ id, place }) => ({
          id,
          coordinates: place?.coordinates
        }))
        .filter((point) => !!point.coordinates) as PointType[],
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [pointsInvalidateKey]
  );

  return (
    <MapAPIContext.Provider
      value={{
        mapAPIRef,
        setMapAPI: (api) => (mapAPIRef.current = api),
        points
      }}
    >
      {children}
    </MapAPIContext.Provider>
  );
}

export const useMapAPI = () => {
  return useContext(MapAPIContext);
};
